import type {EntitlementKey} from '@packages/domains/billing/types';
import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import type {DesignerAbilityConfig} from '../system';

const getSecondaryLocaleEntitlementConfigs = (
  entitlements: EntitlementKey[]
): DesignerAbilityConfig[] => {
  return [
    // Always available in the primary locale
    {
      localization: {
        currentLocaleType: {
          primary: true,
        },
      },
    },
    // Or if you have the entitlements while in a secondary locale
    {
      localization: {
        currentLocaleType: {
          secondary: true,
        },
      },
      entitlements,
    },
  ];
};

export const ONLY_ON_PRIMARY_LOCALE = {
  currentLocaleType: {primary: true},
};

export const canModifyURLs = getSecondaryLocaleEntitlementConfigs([
  ENTITLEMENT_KEYS.CAN_LOCALIZE_URLS,
]);

export const canModifyElementVisibility = getSecondaryLocaleEntitlementConfigs([
  ENTITLEMENT_KEYS.CAN_LOCALIZE_ELEMENT_VISIBILITY,
]);

export const canModifyImageElements = getSecondaryLocaleEntitlementConfigs([
  ENTITLEMENT_KEYS.CAN_LOCALIZE_IMAGE_ELEMENTS,
]);

export const canUseLocalizationBrowserDetection = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.CAN_USE_LOCALIZATION_BROWSER_DETECTION],
  },
] as const);

export const canUseAdvancedLocalization = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.CAN_USE_ADVANCED_LOCALIZATION],
  },
] as const);

export const canEnableLocalePublishing = defineAbilityConfig([
  // If you're NOT an enterprise site, you have to have access to the canEnableLocalePublishing entitlement
  {
    entitlements: [ENTITLEMENT_KEYS.CAN_PUBLISH_LOCALES],
  },
] as const);

export const canEditLocalizedCanvasItem = defineAbilityConfig({
  localization: {
    currentLocaleType: {
      primary: true,
    },
  },
} as const);

export const canAccessLocalizationCorruptionAudit = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.LOCALIZATION_CORRUPTION_RECOVERY]: true,
  },
} as const);

export const canAccessNewAuditPanelUI = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.AUDIT_PANEL_UI_UPDATE]: true,
  },
});

/**
 * Determines whether the user can edit content in the current locale.
 * When locales FGAC is off, all users can edit. When on, requires
 * SiteLocale/editContent authorization for the current locale.
 */
export const canEditLocaleContent = defineAbilityConfig([
  {abilities: {canAccessLocalesFgac: false}},
  {
    abilities: {canAccessLocalesFgac: true},
    authorizations: {'SiteLocale/editContent': true},
  },
] as const);

/**
 * Whether the user has editContent access on ALL site locales.
 * Required for cross-locale operations (delete page, merge/create branch).
 *
 * Dual-path:
 *  - FGA OFF: always true (no locale restrictions exist)
 *  - FGA ON: completeness check across all site locales
 */
export const hasUnrestrictedLocaleAccess = defineAbilityConfig([
  {
    abilities: {canAccessLocalesFgac: false},
  },
  {
    abilities: {canAccessLocalesFgac: true},
    hasUnrestrictedLocaleAccess: true,
  },
] as const);

// Mirrors the auto-translate-on-open endpoint's site-level `read` check.
export const canRequestAutoTranslateOnOpen = defineAbilityConfig({
  userTypes: {authorizedInDesigner: true},
  permission: ['read', 'site'],
} as const);
